export default function Home() {
  return (
    <main className="min-h-screen bg-background-50">
      {/* Hero Section */}
      <section className="relative w-full bg-background-50 pt-[120px] pb-[100px]">
        <div className="max-w-[720px] mx-auto px-6">
          {/* Micro label */}
          <div className="flex items-center justify-center gap-3 mb-[48px]">
            <span className="text-xs tracking-[0.12em] uppercase text-foreground-500 font-medium" style={{ fontFamily: "var(--font-label)" }}>
              Professional Engineering &amp; Digital Solutions
            </span>
          </div>

          {/* Company Title — the only appearance of the name on the entire page */}
          <h1 className="text-center font-heading text-[72px] md:text-[80px] leading-[1.1] tracking-[-0.015em] text-foreground-950">
            PT SOFTWARE<br />LABS LIMITED
          </h1>

          {/* Subtitle */}
          <p className="mt-[28px] text-center text-lg md:text-xl leading-relaxed text-foreground-700 max-w-[640px] mx-auto font-light" style={{ fontFamily: "var(--font-body)" }}>
            A professional engineering team focused on designing and developing modern digital software solutions for the iOS platform and related technologies.
          </p>
        </div>

        {/* Hairline separator */}
        <div className="max-w-[720px] mx-auto px-6 mt-[100px]">
          <div className="w-full h-px bg-foreground-200/60" />
        </div>
      </section>

      {/* About / Core Narrative Section */}
      <section className="relative w-full bg-background-100 py-[100px] md:py-[120px]">
        <div className="max-w-[720px] mx-auto px-6">
          {/* Section identifier */}
          <p className="text-center text-xs tracking-[0.12em] uppercase text-foreground-400 font-medium" style={{ fontFamily: "var(--font-label)" }}>
            Who We Are
          </p>

          <div className="mt-[64px] space-y-10">
            <p className="text-center text-[22px] md:text-2xl leading-[1.7] text-foreground-900 font-light" style={{ fontFamily: "var(--font-body)" }}>
              We build scalable, maintainable, and high-performance systems with a strong focus on usability, stability, and long-term technical quality. Every solution we design is grounded in rigorous engineering discipline and a deep understanding of how people interact with technology.
            </p>

            <p className="text-center text-[22px] md:text-2xl leading-[1.7] text-foreground-900 font-light" style={{ fontFamily: "var(--font-body)" }}>
              Our team follows modern software engineering practices, including clean architecture, performance optimization, and user-centered design. We believe that great software emerges from clarity of purpose, thoughtful structure, and an unwavering commitment to quality at every layer of the stack.
            </p>

            <p className="text-center text-[22px] md:text-2xl leading-[1.7] text-foreground-900 font-light" style={{ fontFamily: "var(--font-body)" }}>
              Continuous innovation drives everything we do. We actively explore new technologies, improve development workflows, and adopt up-to-date engineering standards to deliver reliable and future-ready IT solutions. Our internal processes evolve continually, ensuring we remain at the forefront of technical excellence.
            </p>
          </div>
        </div>
      </section>

      {/* Engineering Philosophy Section */}
      <section className="relative w-full bg-background-50 py-[100px] md:py-[120px]">
        <div className="max-w-[960px] mx-auto px-6">
          <p className="text-center text-xs tracking-[0.12em] uppercase text-foreground-400 font-medium" style={{ fontFamily: "var(--font-label)" }}>
            Our Approach
          </p>

          <div className="mt-[64px] grid grid-cols-1 md:grid-cols-3 gap-x-12 gap-y-14">
            {/* Pillar 1 */}
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-6 flex items-center justify-center">
                <i className="ri-stack-line text-3xl text-accent-500"></i>
              </div>
              <h3 className="font-heading text-xl md:text-2xl text-foreground-950 mb-4 tracking-tight">
                Scalable Architecture
              </h3>
              <p className="text-sm md:text-base leading-relaxed text-foreground-600 font-light" style={{ fontFamily: "var(--font-body)" }}>
                Systems designed from the ground up to grow with your needs, maintaining performance and reliability as demands increase.
              </p>
            </div>

            {/* Pillar 2 */}
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-6 flex items-center justify-center">
                <i className="ri-focus-3-line text-3xl text-accent-500"></i>
              </div>
              <h3 className="font-heading text-xl md:text-2xl text-foreground-950 mb-4 tracking-tight">
                User-Centered Design
              </h3>
              <p className="text-sm md:text-base leading-relaxed text-foreground-600 font-light" style={{ fontFamily: "var(--font-body)" }}>
                Every interface is shaped by how real people think and work, ensuring intuitive, stable, and accessible experiences.
              </p>
            </div>

            {/* Pillar 3 */}
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-6 flex items-center justify-center">
                <i className="ri-loop-left-line text-3xl text-accent-500"></i>
              </div>
              <h3 className="font-heading text-xl md:text-2xl text-foreground-950 mb-4 tracking-tight">
                Continuous Evolution
              </h3>
              <p className="text-sm md:text-base leading-relaxed text-foreground-600 font-light" style={{ fontFamily: "var(--font-body)" }}>
                Internal processes and technical standards are regularly refined to stay aligned with emerging engineering best practices.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer / Contact Section */}
      <footer className="relative w-full bg-background-50 py-10 md:py-14 px-4 md:px-10">
        <div className="max-w-[1200px] mx-auto bg-foreground-950 rounded-[32px] px-8 md:px-[72px] pt-[72px] pb-10">
          {/* Upper Content Zone — Three columns */}
          <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1.5fr] gap-y-10 md:gap-x-8">
            {/* Left Column — Address */}
            <div>
              <h2 className="font-heading text-[36px] md:text-[40px] leading-[1.2] text-background-50 tracking-tight">
                Contact
              </h2>
              <div className="mt-10 space-y-1">
                <p className="text-sm md:text-[15px] leading-[1.75] text-foreground-300 font-light" style={{ fontFamily: "var(--font-body)" }}>
                  36A Cheltenham Road
                </p>
                <p className="text-sm md:text-[15px] leading-[1.75] text-foreground-300 font-light" style={{ fontFamily: "var(--font-body)" }}>
                  Auckland
                </p>
                <p className="text-sm md:text-[15px] leading-[1.75] text-foreground-300 font-light" style={{ fontFamily: "var(--font-body)" }}>
                  0620
                </p>
                <p className="text-sm md:text-[15px] leading-[1.75] text-foreground-300 font-light" style={{ fontFamily: "var(--font-body)" }}>
                  New Zealand
                </p>
              </div>
              <div className="mt-4">
                <p className="text-sm md:text-[15px] leading-[1.75] text-foreground-300 font-light" style={{ fontFamily: "var(--font-body)" }}>
                  ceo@ptsoftwarelabs.com
                </p>
              </div>
            </div>

            {/* Middle Column — Telephone */}
            <div>
              <p className="text-xs tracking-[0.1em] uppercase text-background-50 font-medium" style={{ fontFamily: "var(--font-label)" }}>
                Telephone
              </p>
              <p className="mt-4 text-base md:text-lg text-background-50 font-light" style={{ fontFamily: "var(--font-body)" }}>
                +61 488856206
              </p>
            </div>

            {/* Right Column — Email */}
            <div>
              <p className="text-xs tracking-[0.1em] uppercase text-background-50 font-medium" style={{ fontFamily: "var(--font-label)" }}>
                Email
              </p>
              <p className="mt-4 text-base md:text-lg text-background-50 font-light" style={{ fontFamily: "var(--font-body)" }}>
                ceo@ptsoftwarelabs.com
              </p>
            </div>
          </div>

          {/* Lower Information Bar */}
          <div className="mt-[56px] grid grid-cols-3 items-center">
            <div />
            <p className="text-center text-xs md:text-[13px] text-foreground-500 font-light" style={{ fontFamily: "var(--font-body)" }}>
              2026
            </p>
            <div />
          </div>
        </div>
      </footer>
    </main>
  );
}